import { useQuery } from "@tanstack/react-query";
import { Card, Col, Row, Typography } from "antd";
import React, { useEffect, useMemo } from "react";
import { currencyFormat } from "../../../../../utils/media";
import BookingCard from "../BookingCard";
import BookingFunnelMain from "../BookingFunnelMain";
import BookingsByAge from "../BookingsByAge";
import BookingsPerMonth from "../BookingsPerMonth";
import { bookingOverView } from "../ducks/service";
import PaidBookingsCard from "../PaidBookings";
import "./index.css";
import { getBookingsWidgetAccess } from "./utili";
import { useDashboardFilterContext } from "../../hooks/DashboardFilterProvider";

const { Title } = Typography;

const BookingOverview = React.memo((props: any) => {
  const { widgetAccess } = props;

  const filterData = useDashboardFilterContext();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["bookings", filterData?.queryParams],
    queryFn: () =>
      bookingOverView(filterData?.queryString ? filterData?.queryString : "?"),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false, //  disable auto refetch when window regains focus
  });

  const bookingData = [
    {
      key: "totalBookings",
      title: "Total Bookings",
      value: data?.result?.totalBookings ? data?.result?.totalBookings : 0,
      extraText: currencyFormat(data?.result?.totalAmount > 0 ? data?.result?.totalAmount : 0),
      style: {
        backgroundColor: "#123A55",
        color: "#ffffff",
      },
      isLoading: isLoading,
    },
    {
      key: "unpaidBookings",
      title: "Unpaid Bookings",
      value: data?.result?.unpaidBookings ? data?.result?.unpaidBookings : 0,
      extraText: currencyFormat(data?.result?.unpaidAmount ? data?.result?.unpaidAmount : 0),
      style: {
        backgroundColor: "#ffffff",
        color: "#123A55",
        border: "1px solid #E9E9E9",
      },
      isLoading: isLoading,
    },
    {
      key: "paidBookings",
      title: "Paid Bookings",
      value: data?.result?.paidBookings ? data?.result?.paidBookings : 0,
      extraText: currencyFormat(data?.result?.paidAmount ? data?.result?.paidAmount : 0),
      style: {
        backgroundColor: "#4682A9",
        color: "#ffffff",
      },
      isLoading: isLoading,
    },

    {
      key: "expiredBookings",
      title: "Expired Bookings",
      value: data?.result?.expiredBookings ? data?.result?.expiredBookings : 0,
      extraText: currencyFormat(data?.result?.expiredAmount ? data?.result?.expiredAmount : 0),
      style: {
        backgroundColor: "#F8F8F8",
        color: "#123A55",
      },
      isLoading: isLoading,
    },
    {
      key: "cancelledBookings",
      title: "Cancelled Bookings",
      value: data?.result?.cancelledBookings ? data?.result?.cancelledBookings : 0,
      extraText: currencyFormat(data?.result?.cancelledAmount ? data?.result?.cancelledAmount : 0),
      style: {
        backgroundColor: "#F8F8F8",
        color: "#123A55",
      },
      isLoading: isLoading,
    },
  ];

  return (
    <Card
      style={{
        background: "#ffffff",
        margin: "0 16px",
        borderRadius: "16px",
        border: "none",
      }}
    >
      <Title level={4} style={{ marginBottom: 24 }}>
        <span style={{ fontWeight: 700, color: "#1F5782" }}>BOOKING</span>{" "}
        <span style={{ fontWeight: 400, color: "#4A7A99" }}>OVERVIEW</span>
      </Title>

      {getBookingsWidgetAccess(widgetAccess, "bookingOverView") && (
        <Row gutter={[8, 8]} justify="space-between">
          {bookingData.map((item, index) => (
            <Col
              key={item.key}
              xs={24}
              sm={12}
              style={{ display: "flex" }}
              md={index === 0 ? 6 : index === 1 ? 5 : 4}
              lg={index === 0 ? 6 : index === 1 ? 5 : 4}
            >
              <BookingCard {...item} />
            </Col>
          ))}
        </Row>
      )}

      <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
        {getBookingsWidgetAccess(widgetAccess, "bookingperMonth") && (
          <Col xs={24} sm={12} md={8} lg={8}>
            <BookingsPerMonth />
          </Col>
        )}

        {getBookingsWidgetAccess(widgetAccess, "bookingFunnel") && (
          <Col xs={24} sm={12} md={16} lg={16} flex="auto">
            <BookingFunnelMain />
          </Col>
        )}
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
        {getBookingsWidgetAccess(widgetAccess, "getPaidMetrics") && (
          <Col xs={24} sm={12} md={8}>
            <PaidBookingsCard title={"PAID"} subTitle={"BOOKINGS"} score={24} />
          </Col>
        )}
        {getBookingsWidgetAccess(widgetAccess, "getUnPaidMetrics") && (
          <Col xs={24} sm={12} md={8}>
            <PaidBookingsCard title={"UNPAID"} subTitle={"BOOKINGS"} score={7} />
          </Col>
        )}
        {getBookingsWidgetAccess(widgetAccess, "bookingByAge") && (
          <Col xs={24} sm={12} md={8}>
            <BookingsByAge />
          </Col>
        )}
      </Row>
    </Card>
  );
});

export default BookingOverview;
